import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

function increaseLegalDocFontSizes(cssText, increasePx = 2) {
  return String(cssText || '')
    .replace(/(\bfont:\s*)(\d+(?:\.\d+)?)px\b/g, (_, prefix, size) => {
      return `${prefix}${Number(size) + increasePx}px`;
    })
    .replace(/(\bfont-size:\s*)(\d+(?:\.\d+)?)px\b/g, (_, prefix, size) => {
      return `${prefix}${Number(size) + increasePx}px`;
    });
}

export default function HtmlLegalDocumentPage({ documentPath }) {
  const [documentHtml, setDocumentHtml] = useState('');
  const [documentStyles, setDocumentStyles] = useState('');
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let isMounted = true;

    async function loadDocument() {
      setStatus('loading');

      try {
        const response = await fetch(documentPath);

        if (!response.ok) {
          throw new Error(`Failed to load legal document: ${documentPath}`);
        }

        const rawHtml = await response.text();
        const parser = new DOMParser();
        const parsed = parser.parseFromString(rawHtml, 'text/html');

        if (!isMounted) {
          return;
        }

        setDocumentStyles(
          increaseLegalDocFontSizes(parsed.querySelector('style')?.textContent ?? '')
        );
        setDocumentHtml(parsed.body?.innerHTML ?? '');
        setStatus('ready');
      } catch (error) {
        console.error(error);

        if (isMounted) {
          setStatus('error');
        }
      }
    }

    loadDocument();

    return () => {
      isMounted = false;
    };
  }, [documentPath]);

  return (
    <div className="min-h-screen bg-white text-black">
      <header className="fixed top-0 left-0 z-50 flex w-full items-center justify-between bg-white/90 px-4 py-5 backdrop-blur-md border-b border-black/5 md:px-8 lg:px-12">
        <Link to="/" className="text-2xl font-bold tracking-widest text-black hover:opacity-80 transition-opacity">
          FAUS
        </Link>
        <Link
          to="/"
          className="flex items-center gap-2 text-sm font-semibold text-neutral-600 transition-colors hover:text-black"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Volver al inicio
        </Link>
      </header>

      <main className="mx-auto max-w-5xl px-6 pb-24 pt-32 md:px-8">
        <div className="rounded-3xl border border-black/10 bg-white p-8 shadow-sm md:p-12">
          {status === 'loading' ? <p>Cargando documento...</p> : null}
          {status === 'error' ? (
            <p>No pudimos cargar este documento ahora mismo. Intenta nuevamente en unos minutos.</p>
          ) : null}

          {status === 'ready' ? (
            <>
              <style>{`
                .legal-document {
                  color: #333333;
                }

                .legal-document table {
                  width: 100%;
                  margin: 12px 0;
                }

                .legal-document td,
                .legal-document th {
                  vertical-align: top;
                }

                .legal-document a {
                  color: inherit;
                }

                ${documentStyles}
              `}</style>
              <div
                className="legal-document overflow-x-auto"
                dangerouslySetInnerHTML={{ __html: documentHtml }}
              />
            </>
          ) : null}
        </div>
      </main>
    </div>
  );
}
